import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Represents a specific Product Category (e.g., "Adjustable Pallet Racking").
 * These are returned as nested items within ProductType in the backend.
 */
export interface Category {
  _id: string;
  name: string;
  image?: string;
  slug: string;
  product_type?: string;
  is_active?: number;
}

/**
 * Represents a Product Type (e.g., "Pallet Racking").
 * Each Product Type acts as a group for multiple categories.
 */
export interface ProductType {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  categories: Category[]; // Array of sub-categories
}

interface CategoriesState {
  productTypes: ProductType[]; // Formerly referred to as 'categories' at the root level
}

const initialState: CategoriesState = {
  productTypes: [],
};

const categoriesSlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    // Dispatched by AdminIndex after fetching GetProductTypesAPI
    setCategories: (state, action: PayloadAction<ProductType[]>) => {
      state.productTypes = action.payload;
    },
  },
});

export const { setCategories } = categoriesSlice.actions;

/**
 * Selects the top-level list of product types.
 */
export const selectProductTypes = (state: { categories: CategoriesState }) =>
  state.categories.productTypes;

/**
 * Alias for selectProductTypes to maintain compatibility.
 */
export const selectCategories = selectProductTypes;

/**
 * Generates options for Ant Design Select with OptGroup support.
 * The Product Type becomes the group label, and its categories become the selectable options.
 */
export const generateCategoryOptGroupOptions = (state: {
  categories: CategoriesState;
}) => {
  return (state.categories?.productTypes || []).map((type: ProductType) => ({
    label: type.name,
    title: type.name,
    options: (type.categories || []).map((cat: Category) => ({
      label: cat.name,
      value: cat._id,
    })),
  }));
};

export default categoriesSlice.reducer;
